import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiGrid, FiList } from "react-icons/fi";
import JobSearchBar from "../components/HeaderFilter"; // your search bar component
import JobCard from "../components/JobCard";
import Pagination from "../components/Pagination";
import SkeletonCard from "../components/SkeletonCard";
import { fetchAllJobs } from "../services/jobsApi";
import "../styles/JobList.css";

const JOBS_PER_PAGE = 6;

export default function JobListingPage() {
  const navigate = useNavigate();
  const [allJobs, setAllJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filters, setFilters] = useState({
    "Sort By": [],
    "Date Posted": [],
    Experience: [],
    "Job Type": [],
    Location: [],
    searchText: "",
  });

  const [viewMode, setViewMode] = useState("grid"); // 'grid' or 'list'
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setLoading(true);
    fetchAllJobs()
      .then((jobs) => {
        setAllJobs(jobs);
        setFilteredJobs(jobs);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  // Filtering logic (same as before)
  useEffect(() => {
    if (!allJobs.length) return;
    let result = [...allJobs];

    const q = (filters.searchText || "").trim().toLowerCase();
    if (q) {
      result = result.filter(
        (j) =>
          j.title.toLowerCase().includes(q) ||
          j.company.toLowerCase().includes(q) ||
          (j.skills || []).some((s) => s.toLowerCase().includes(q))
      );
    }

    if (filters["Job Type"]?.length) {
      result = result.filter(
        (j) =>
          filters["Job Type"].includes("All") ||
          filters["Job Type"].includes(j.jobType)
      );
    }

    if (filters.Location?.length && !filters.Location.includes("All")) {
      result = result.filter((j) => {
        const loc = j.location.toLowerCase();
        return filters.Location.some((opt) => {
          if (opt === "Onsite") return !loc.includes("remote") && !loc.includes("hybrid");
          if (opt === "Remote") return loc.includes("remote");
          if (opt === "Hybrid") return loc.includes("hybrid");
          return loc.includes(opt.toLowerCase());
        });
      });
    }

    if (filters.Experience?.length && !filters.Experience.includes("All")) {
      result = result.filter((j) =>
        filters.Experience.some((exp) => {
          if (exp === "Fresher (0 years)") return j.yoe.toLowerCase().includes("0");
          if (exp === "Internship") return j.jobType.toLowerCase().includes("intern");
          return j.yoe.toLowerCase().includes(exp.split(" ")[0]);
        })
      );
    }

    if (filters["Date Posted"]?.length && !filters["Date Posted"].includes("All")) {
      const now = new Date();
      result = result.filter((j) => {
        const posted = new Date(j.postedAt);
        return filters["Date Posted"].some((dopt) => {
          if (dopt === "Past 1 hour") return now - posted <= 1 * 60 * 60 * 1000;
          if (dopt === "Past 12 hours") return now - posted <= 12 * 60 * 60 * 1000;
          if (dopt === "Past 24 hours") return now - posted <= 24 * 60 * 60 * 1000;
          if (dopt === "Past 2 days") return now - posted <= 2 * 24 * 60 * 60 * 1000;
          if (dopt === "Past 7 days") return now - posted <= 7 * 24 * 60 * 60 * 1000;
          return true;
        });
      });
    }

    if (filters["Sort By"]?.length) {
      const sortOpt = filters["Sort By"][0];
      if (sortOpt === "Latest") {
        result.sort((a, b) => new Date(b.postedAt) - new Date(a.postedAt));
      } else if (sortOpt === "Oldest") {
        result.sort((a, b) => new Date(a.postedAt) - new Date(b.postedAt));
      }
    }

    setFilteredJobs(result);
    setCurrentPage(1);
  }, [filters, allJobs]);

  const totalPages = Math.max(1, Math.ceil(filteredJobs.length / JOBS_PER_PAGE));
  const start = (currentPage - 1) * JOBS_PER_PAGE;
  const pageJobs = filteredJobs.slice(start, start + JOBS_PER_PAGE);

  const handleLogout = () => {
    navigate("/"); // Back to RoleSelectionPage
  };

  return (
    <div className="page-root">
      <div className="page-header">
        <button onClick={()=>navigate("/candidate-dashboard")}  className="page-back" >←</button>
        <h1>StrideNext Jobs</h1>
        <div className="header-controls" aria-hidden>
          {/* view toggle moved to header-controls so it's clickable and not overlapped */}
          <div className="view-toggle" role="toolbar" aria-label="View mode">
            <button
              className={viewMode === "grid" ? "vt-btn active" : "vt-btn"}
              onClick={() => setViewMode("grid")}
              title="Grid View"
              aria-pressed={viewMode === "grid"}
            >
              <FiGrid size={18} />
            </button>
            <button
              className={viewMode === "list" ? "vt-btn active" : "vt-btn"}
              onClick={() => setViewMode("list")}
              title="List View"
              aria-pressed={viewMode === "list"}
            >
              <FiList size={18} />
            </button>
          </div>
        </div>
      </div>

      <div className="search-toggle-row">
        <div className="search-wrapper">
          <JobSearchBar filters={filters} setFilters={setFilters} />
        </div>
        {/* Redundant view-toggle removed from inside searchbar; header-controls above used */}
      </div>

      <div className={`job-cards ${viewMode}`}>
        {loading
          ? Array.from({ length: JOBS_PER_PAGE }).map((_, i) => <SkeletonCard key={i} />)
          : pageJobs.map((job) => <JobCard key={job.id} job={job} layout={viewMode} />)}
      </div>

      <div className="pagination-wrapper">
        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
      </div>
    </div>
  );
}
